import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import Map from "./Map";
import PingDeviceModal from "./PingDeviceModal";
import IntervalControlModal from "./IntervalControlModal";

const Dashboard = () => {
  const [devices, setDevices] = useState([]);
  const [faultCount, setFaultCount] = useState(0);
  const [voltageAlertCount, setVoltageAlertCount] = useState(0);
  const [isModalVisible, setModalVisible] = useState(false);
  const [isIntervalModalVisible, setIntervalModalVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDevices = () => {
      fetch("/api/devices")
        .then((res) => res.json())
        .then((data) => setDevices(data))
        .catch((err) => console.error("Failed to load devices", err));
    };

    fetchDevices();
    const interval = setInterval(fetchDevices, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchFaults = () => {
      fetch("/api/fault-zones")
        .then((res) => res.json())
        .then((data) => {
          const now = Date.now();
          const countRecent = data.filter((z) => {
            const ts = new Date(z.timestamps?.[0] || z.timeWindow?.start).getTime();
            return now - ts < 24 * 60 * 60 * 1000;
          }).length;
          setFaultCount(countRecent);
        })
        .catch((err) => console.error("Failed to fetch fault zones", err));
    };

    const fetchAlertCount = () => {
      fetch("/api/filtered/alerts")
        .then((res) => res.json())
        .then((data) => {
          const recent = data.filter((a) => {
            const ts = new Date(a.timestamp).getTime();
            return Date.now() - ts < 24 * 60 * 60 * 1000;
          });
          const uniqueDeviceIds = [...new Set(recent.map((a) => a.deviceId))];
setVoltageAlertCount(uniqueDeviceIds.length);
        })
        .catch((err) => console.error("Failed to fetch alerts", err));
    };

    fetchFaults();
    fetchAlertCount();
    const interval = setInterval(() => {
      fetchFaults();
      fetchAlertCount();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const countByStatus = (type) => {
    switch (type) {
      case "online":
        return devices.filter((d) => d.status === "online").length;
      case "offline":
        return devices.filter((d) => d.status === "offline").length;
      case "outages":
        return devices.filter((d) => d.status === "outage").length;
      case "blinks":
        return devices.filter((d) => {
          if (!d.lastBlink) return false;
          const blinkTime = new Date(d.lastBlink);
          const now = new Date();
          const diffMs = now - blinkTime;
          return diffMs < 24 * 60 * 60 * 1000;
        }).length;
      default:
        return 0;
    }
  };

  const Card = ({ icon, label, count, route, color }) => (
    <div
      onClick={() => navigate(route)}
      className="card"
      style={{
        borderTop: `4px solid ${color}`,
      }}
    >
      <div className="card-icon">{icon}</div>
      <h2>{count}</h2>
      <p>{label}</p>
    </div>
  );

  const handleOpenModal = () => setModalVisible(true);
  const handleCloseModal = () => setModalVisible(false);

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="dashboard-content">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h2 className="dashboard-header">Dashboard</h2>
          <div style={{ display: "flex", gap: "1rem" }}>
            <button onClick={handleOpenModal} style={pingButtonStyle}>Ping Device</button>
            <button onClick={() => setIntervalModalVisible(true)} style={intervalButtonStyle}>
              Device Interval Control
            </button>
          </div>
        </div>

        <div className="card-container">
          <Card
            icon="🟢"
            label="Online Devices"
            count={countByStatus("online")}
            route="/filtered/online"
            color="#28a745"
          />
          <Card
            icon="🔵"
            label="Offline Devices"
            count={countByStatus("offline")}
            route="/filtered/offline"
            color="#007bff"
          />
          <Card
            icon="🛑"
            label="Outage Alerts"
            count={countByStatus("outages")}
            route="/filtered/outages"
            color="#ff3b3b"
          />
          <Card
            icon="⚡"
            label="Voltage Alerts"
            count={voltageAlertCount}
            route="/filtered/alerts"
            color="#ffc107"
          />
          <Card
            icon="✨"
            label="Blink Alerts"
            count={countByStatus("blinks")}
            route="/filtered/blinks"
            color="#17a2b8"
          />
          <Card
            icon="⚠️"
            label="Predicted Faults"
            count={faultCount}
            route="/fault-zones"
            color="#ff5722"
          />
        </div>

        <h3 style={{ marginTop: "2rem" }}>Map View</h3>
        <div className="map-view">
          <Map devices={devices} />
        </div>
      </div>

      <PingDeviceModal show={isModalVisible} onClose={handleCloseModal} />
      <IntervalControlModal
        show={isIntervalModalVisible}
        onClose={() => setIntervalModalVisible(false)}
      />
    </div>
  );
};

const pingButtonStyle = {
  backgroundColor: "#28a745",
  color: "white",
  padding: "10px 20px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
};

const intervalButtonStyle = {
  backgroundColor: "#007bff",
  color: "white",
  padding: "10px 20px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
};

export default Dashboard;
